/**
 * Body-portalled `ImageLightbox` with Escape-to-close, shared by the Evidence
 * sub-agent's thumbnail strip and its inline anchored figures so both overlays
 * dismiss identically. Renders nothing when `image` is null.
 */

import { ImageLightbox } from '@heidi/evidence';
import type { EvidenceImage } from '@heidi/evidence/contracts';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface EvidenceLightboxPortalProps {
  image: EvidenceImage | null;
  onClose: () => void;
}

export const EvidenceLightboxPortal = ({
  image,
  onClose,
}: EvidenceLightboxPortalProps): JSX.Element | null => {
  const open = image !== null;
  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    // Capture + preventDefault claims Esc, so page-wide Esc bindings (e.g. stop generating) stand down.
    document.addEventListener('keydown', handleKey, { capture: true });
    return () => document.removeEventListener('keydown', handleKey, { capture: true });
  }, [open, onClose]);

  if (!image) return null;
  return createPortal(<ImageLightbox image={image} onClose={onClose} />, document.body);
};
